import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { getAllTutors, getAllResearchGroups } from '../services/firestore'
import { Mail, BookOpen, Loader, User, Microscope } from 'lucide-react'
import { capitalizeName } from '../utils/formatters'
import './Tutors.css'

const Tutors = () => {
    const [tutors, setTutors] = useState([])
    const [filterType, setFilterType] = useState('all')
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)
    const navigate = useNavigate()

    const [allResearchGroups, setAllResearchGroups] = useState([])

    useEffect(() => {
        loadData()
    }, [])

    const loadData = async () => {
        setLoading(true)
        try {
            const [tutorsResult, groupsResult] = await Promise.all([
                getAllTutors(),
                getAllResearchGroups()
            ])
            
            if (tutorsResult.success) setTutors(tutorsResult.data)
            else setError(tutorsResult.error)
            
            if (groupsResult.success) setAllResearchGroups(groupsResult.data)

        } catch (err) {
            setError(err.message)
        }
        setLoading(false)
    }

    const handleTutorClick = (tutorId) => {
        navigate(`/tutor/${tutorId}`)
    }

    if (loading) {
        return (
            <div className="tutors-page">
                <div className="loading-container">
                    <Loader size={48} className="spinner" />
                    <p>Cargando tutores...</p>
                </div>
            </div>
        )
    }

    if (error) {
        return (
            <div className="tutors-page">
                <div className="error-container">
                    <p>Error al cargar tutores: {error}</p>
                </div>
            </div>
        )
    }

    const getTutorRoles = (tutor) => {
        if (!tutor) return []
        if (tutor.tutorRoles && Array.isArray(tutor.tutorRoles)) {
            return tutor.tutorRoles
        }
        if (tutor.tutorType === 'eucs') return ['eucs']
        if (tutor.tutorType === 'standard') return ['vtic']
        if (tutor.tutorType === 'aurora') return ['aurora']
        if (tutor.tutorType === 'phuma') return ['phuma']
        return ['vtic']
    }

    const filteredTutors = tutors.filter(tutor => {
        if (tutor.isRetired === true) return false
        const roles = getTutorRoles(tutor)
        if (filterType === 'all') return true
        if (filterType === 'standard' && (roles.includes('vtic') || !tutor.tutorType)) return true
        if (filterType === 'eucs' && roles.includes('eucs')) return true
        if (filterType === 'aurora' && roles.includes('aurora')) return true
        if (filterType === 'phuma' && roles.includes('phuma')) return true
        return false
    })

    // Ordenar tutores: acreditados primero, luego por cantidad de roles
    filteredTutors.sort((a, b) => {
        const aAcc = a.isAccredited === true ? 1 : 0;
        const bAcc = b.isAccredited === true ? 1 : 0;
        if (bAcc !== aAcc) {
            return bAcc - aAcc;
        }
        if (filterType === 'all') {
            return getTutorRoles(b).length - getTutorRoles(a).length;
        }
        return 0;
    })

    return (
        <div className="tutors-page">
            <div className="tutors-header">
                <h1>Nuestros Tutores</h1>
                <p>Conoce a nuestro equipo de tutores especializados</p>
                <div className="tutors-filter">
                    <button 
                        className={filterType === 'all' ? 'active' : ''} 
                        onClick={() => setFilterType('all')}
                    >
                        Todos
                    </button>
                    <button 
                        className={filterType === 'standard' ? 'active' : ''} 
                        onClick={() => setFilterType('standard')}
                    >
                        Lucem Aspicio
                    </button>
                    <button 
                        className={filterType === 'aurora' ? 'active' : ''} 
                        onClick={() => setFilterType('aurora')}
                    >
                        Aurora
                    </button>
                    <button 
                        className={filterType === 'phuma' ? 'active' : ''} 
                        onClick={() => setFilterType('phuma')}
                    >
                        Phuma
                    </button>
                    <button 
                        className={filterType === 'eucs' ? 'active' : ''} 
                        onClick={() => setFilterType('eucs')}
                    >
                        EUCS
                    </button>
                </div>
            </div>

            <div className="tutors-grid">
                {filteredTutors.map(tutor => {
                    const roles = getTutorRoles(tutor)

                    return (
                        <div
                            key={tutor.id}
                            className={`tutor-card ${roles.includes('eucs') ? 'tutor-eucs-theme' : 'tutor-vtic-theme'}`}
                            onClick={() => handleTutorClick(tutor.id)}
                        >
                            <div className="tutor-avatar">
                                {tutor.photoURL ? (
                                    <img src={tutor.photoURL} alt={capitalizeName(tutor.fullName)} />
                                ) : (
                                    <div className="avatar-placeholder">
                                        <User size={40} />
                                    </div>
                                )}
                            </div>

                            {tutor.isAccredited && (
                                <span style={{
                                    background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
                                    color: '#38bdf8',
                                    padding: '2px 8px',
                                    borderRadius: '12px',
                                    fontSize: '10px',
                                    fontWeight: '700',
                                    boxShadow: '0 2px 6px rgba(56, 189, 248, 0.2)',
                                    border: '1px solid #0284c7',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '4px',
                                    marginBottom: '10px',
                                    marginTop: '-4px'
                                }}>
                                    <Microscope size={10} /> Acreditado
                                </span>
                            )}

                            <div className="tutor-info">
                                <h2>{capitalizeName(tutor.fullName) || 'Tutor'}</h2>

                                {/* Etiquetas de roles */}
                                <div className="tutor-roles" style={{ display: 'flex', gap: '5px', flexWrap: 'wrap', marginBottom: '8px', marginTop: '-4px' }}>
                                    {getTutorRoles(tutor).map(role => {
                                    const config = {
                                        vtic: { label: 'Lucem Aspicio', bg: '#11163e', color: '#ffffff' },
                                        aurora: { label: 'Aurora', bg: '#D10056', color: '#ffffff' },
                                        phuma: { label: 'Phuma', bg: '#B35C00', color: '#ffffff' },
                                        eucs: { label: 'EUCS', bg: '#7E161C', color: '#ffffff' }
                                    }[role] || { label: role, bg: '#6b7280', color: '#ffffff' };
                                    return (
                                        <span key={role} style={{
                                            backgroundColor: config.bg,
                                            color: config.color,
                                            padding: '2px 8px',
                                            borderRadius: '12px',
                                            fontSize: '10px',
                                            fontWeight: 'bold',
                                            boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
                                        }}>
                                            {config.label}
                                        </span>
                                    );
                                })}
                            </div>

                            {tutor.researchGroups && tutor.researchGroups.length > 0 && (
                                <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap', marginBottom: '8px', marginTop: '-5px' }}>
                                    <span style={{ backgroundColor: '#1A2B5B', color: 'white', padding: '2px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '3px' }}>
                                        <Microscope size={12} /> Investigador
                                    </span>
                                    {allResearchGroups.filter(g => tutor.researchGroups.includes(g.id)).map(g => (
                                        <span key={g.id} style={{ backgroundColor: '#f0fdfa', border: '1px solid #ccfbf1', color: '#0d9488', padding: '2px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: 'bold' }}>
                                            {g.abbr}
                                        </span>
                                    ))}
                                </div>
                            )}

                            {tutor.bio && (
                                <p className="tutor-bio">{tutor.bio}</p>
                            )}

                            {tutor.courses && tutor.courses.length > 0 && (
                                <div className="tutor-courses">
                                    <div className="courses-header">
                                        <BookOpen size={16} />
                                        <span>Clases</span>
                                    </div>
                                    <div className="courses-list">
                                        {tutor.courses.slice(0, 3).map(course => (
                                            <span key={course.id} className="course-tag">
                                                {course.name}
                                            </span>
                                        ))}
                                        {tutor.courses.length > 3 && (
                                            <span className="course-tag">+{tutor.courses.length - 3}</span>
                                        )}
                                    </div>
                                </div>
                            )}
                            {tutor.email && (
                                <a
                                    href={`mailto:${tutor.email}`}
                                    className="contact-button"
                                    title="Enviar correo"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <Mail size={16} />
                                    Contactar
                                </a>
                            )}
                        </div>
                    </div>
                )})}
            </div>

            {filteredTutors.length === 0 && (
                <div className="empty-state">
                    <User size={64} />
                    <h2>No hay tutores disponibles</h2>
                    <p>No se encontraron tutores con los filtros actuales</p>
                </div>
            )}
        </div>
    )
}

export default Tutors
